// Copyright 2020 Sorama B.V.

import { AnalysisWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/AnalysisWorkflowModuleCatalog";
import { TimelineEntryDTO, TimelineEntryType } from "datatypes/SoramaWebApi";
import { IExportable } from "libraries/Export/IExportable";
import { ProjectRepository } from "repositories/ProjectRepository";
import { TimelineEntriesRepository } from "repositories/TimelineEntriesRepository";
import { ExportService } from "services/Export/ExportService";
import { ImageFormat } from "services/Export/Format";
import { IProjectManagementService } from "services/Project/IProjectManagementService";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleTimelineEvent,
    IWorkflowModuleTimelineEventFactory,
} from "../Interfaces/IWorkflowModule";
import { IWorkflowModuleConfig } from "../Interfaces/IWorkflowModuleConfig";
import { WorkflowPropertyBase } from "./../WorkflowProperties/WorkflowPropertyUriConverter";

export class WorkflowModuleTimelineEventFactory implements IWorkflowModuleTimelineEventFactory {
    private eventType: TimelineEntryType;
    private properties: WorkflowPropertyBase[];
    private projectId: string;
    private registeredEvent: WorkflowModuleTimelineEvent;

    constructor(
        private readonly projectRepository: ProjectRepository,
        private readonly timelineEventRepository: TimelineEntriesRepository,
        private readonly projectManagementService: IProjectManagementService,
        private readonly exportService: ExportService
    ) {}

    public forProject(projectId: string): IWorkflowModuleTimelineEventFactory {
        this.projectId = projectId;
        return this;
    }

    public ofType(eventType: TimelineEntryType): IWorkflowModuleTimelineEventFactory {
        this.eventType = eventType;
        return this;
    }

    public withProperties(properties: WorkflowPropertyBase[]): IWorkflowModuleTimelineEventFactory {
        this.properties = properties;
        return this;
    }

    public register(): Promise<IWorkflowModuleTimelineEvent> {
        if (this.registeredEvent) {
            return Promise.resolve(this.registeredEvent);
        }

        let projectId = this.projectId;

        if (projectId == undefined && this.projectManagementService.currentProject.status.available) {
            projectId = this.projectManagementService.currentProject.item.id;
        } else if (projectId == undefined) {
            return Promise.reject("No project selected");
        }

        if (this.eventType == undefined) {
            return Promise.reject("No event type defined");
        }

        const properties: { [key: string]: unknown } = {};
        if (this.properties) {
            for (const prop of this.properties) {
                properties[prop.name] = prop.value;
            }
        }

        return this.projectRepository
            .createTimelineEntry(projectId, this.eventType, properties)
            .then((timelineEntry) => {
                this.registeredEvent = new WorkflowModuleTimelineEvent(
                    timelineEntry,
                    this.timelineEventRepository,
                    this.exportService
                );
                return this.registeredEvent;
            });
    }
}

export class WorkflowModuleTimelineEvent implements IWorkflowModuleTimelineEvent {
    constructor(
        private timelineEntry: TimelineEntryDTO,
        private readonly timelineRepository: TimelineEntriesRepository,
        private readonly exportService: ExportService
    ) {}

    public setHighlightEvent(highlighted: boolean): Promise<void> {
        return this.timelineRepository.updateHighlighted(this.timelineEntry.id, highlighted).then((x) => {
            this.timelineEntry = x;
        });
    }

    public getDtoType(): TimelineEntryDTO {
        return this.timelineEntry;
    }

    public updateSnapshot(snapshot: string): Promise<void> {
        return this.timelineRepository.updateSnapshot(this.timelineEntry.id, snapshot).then((x) => {
            this.timelineEntry = x;
        });
    }

    public async updateSnapshotFromModule(moduleInstance: IWorkflowModuleInstance): Promise<void> {
        return this.generateSnapshot(moduleInstance).then((snapshotBase64) => {
            return this.updateSnapshot(snapshotBase64);
        });
    }

    public getWorkflowProperties(): WorkflowPropertyBase[] {
        const newProps: WorkflowPropertyBase[] = [];

        for (const property of Object.keys(this.timelineEntry.properties)) {
            const prop: WorkflowPropertyBase = {
                name: property,
                value: this.timelineEntry.properties[property],
            };

            newProps.push(prop);
        }

        return newProps;
    }

    public getWorkflowModuleConfig(): IWorkflowModuleConfig {
        switch (this.timelineEntry.type) {
            case TimelineEntryType.FarfieldAnalysis:
                return AnalysisWorkflowModuleCatalog.FarfieldModule;
            case TimelineEntryType.NearfieldAnalysis:
                return AnalysisWorkflowModuleCatalog.NearfieldModule;
            case TimelineEntryType.TimeAnalysis:
                return AnalysisWorkflowModuleCatalog.TimeModule;
            case TimelineEntryType.SpectrogramAnalysis:
                return AnalysisWorkflowModuleCatalog.SpectrogramModule;
            case TimelineEntryType.SpectrumAnalysis:
                return AnalysisWorkflowModuleCatalog.SpectrumModule;
            case TimelineEntryType.Measurement:
            default:
                return undefined;
        }
    }

    private async generateSnapshot(moduleInstance: IWorkflowModuleInstance): Promise<string> {
        const exportState = moduleInstance.getExportImageState();

        if (exportState == undefined) {
            return Promise.reject("Provided module has no export functionality");
        }

        const exportable: IExportable = exportState.exportable;
        const img = await exportable.exportToSvg(1000, 1000);
        const svgImage = img.render({});
        const renderedImg = await this.exportService.image(ImageFormat.png, svgImage).exportToBuffer();

        let binary = "";
        const bytes = new Uint8Array(renderedImg);
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes[i]);
        }

        return btoa(binary);
    }
}
